import React, { useState, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import {
    FiEye, FiPackage, FiHome, FiShoppingBag, FiScissors,
    FiFilter, FiCalendar, FiUser, FiDollarSign, FiRefreshCw,
    FiChevronLeft, FiChevronRight, FiDownload,
    FiTrendingUp, FiClock, FiCheckCircle, FiXCircle, FiAlertCircle, FiArrowLeft
} from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { apiCall } from '../config/api-request';
import { useNavigate } from 'react-router-dom';

function Orders() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);

    // Filter states
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [userId, setUserId] = useState('');
    const [selectedType, setSelectedType] = useState('ALL');
    const [selectedStatus, setSelectedStatus] = useState('ALL');
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Statistics
    const [statistics, setStatistics] = useState({
        totalOrders: 0,
        totalAmount: 0,
        averageOrderValue: 0,
        statusBreakdown: {}
    });

    // Order types and statuses
    const orderTypes = ['ALL', 'Hotel Service', 'Food Item', 'Salon Service'];
    const orderStatuses = ['ALL', 'Accept', 'Reject', 'Preparing', 'Waiting', 'Complete', 'Cancel'];

    useEffect(() => {
        fetchOrders();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [orders, dateRange, userId, selectedType, selectedStatus, searchTerm]);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const result = await apiCall({ api: 'order-list-admin' }, true);

            if (result.status === true) {
                const ordersData = result.data.orders || [];
                setOrders(ordersData);
                calculateStatistics(ordersData);
            } else {
                toast.error(result.message || "Failed to load orders");
            }
        } catch (error) {
            console.error("Error fetching orders:", error);
            toast.error("Failed to load orders");
        } finally {
            setLoading(false);
        }
    };

    const calculateStatistics = (ordersData) => {
        const totalOrders = ordersData.length;
        const totalAmount = ordersData.reduce((sum, order) => sum + parseFloat(order.amount), 0);
        const averageOrderValue = totalOrders > 0 ? totalAmount / totalOrders : 0;

        const statusBreakdown = {};
        orderStatuses.forEach(status => {
            if (status !== 'ALL') {
                statusBreakdown[status] = ordersData.filter(order => order.status === status).length;
            }
        });

        setStatistics({
            totalOrders,
            totalAmount,
            averageOrderValue,
            statusBreakdown
        });
    };

    const applyFilters = () => {
        let filtered = [...orders];

        // Filter by date range
        if (startDate && endDate) {
            filtered = filtered.filter(order => {
                const orderDate = new Date(order.date);
                return orderDate >= startDate && orderDate <= endDate;
            });
        }

        // Filter by user ID
        if (userId.trim()) {
            filtered = filtered.filter(order =>
                order.user_id && order.user_id.toString().includes(userId)
            );
        }

        // Filter by type
        if (selectedType !== 'ALL') {
            filtered = filtered.filter(order => order.type === selectedType);
        }

        // Filter by status
        if (selectedStatus !== 'ALL') {
            filtered = filtered.filter(order => order.status === selectedStatus);
        }

        // Filter by search term (order ID)
        if (searchTerm.trim()) {
            filtered = filtered.filter(order =>
                order.order_id && order.order_id.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        setFilteredOrders(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setDateRange([null, null]);
        setUserId('');
        setSelectedType('ALL');
        setSelectedStatus('ALL');
        setSearchTerm('');
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'accept': return 'bg-green-100 text-green-800';
            case 'reject': return 'bg-red-100 text-red-800';
            case 'preparing': return 'bg-blue-100 text-blue-800';
            case 'waiting': return 'bg-yellow-100 text-yellow-800';
            case 'complete': return 'bg-purple-100 text-purple-800';
            case 'cancel': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getTypeIcon = (type) => {
        switch (type?.toLowerCase()) {
            case 'hotel service': return <FiHome className="w-4 h-4" />;
            case 'food item': return <FiShoppingBag className="w-4 h-4" />;
            case 'salon service': return <FiScissors className="w-4 h-4" />;
            default: return <FiPackage className="w-4 h-4" />;
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'accept': return <FiCheckCircle className="w-4 h-4" />;
            case 'reject': return <FiXCircle className="w-4 h-4" />;
            case 'preparing': return <FiClock className="w-4 h-4" />;
            case 'waiting': return <FiClock className="w-4 h-4" />;
            case 'complete': return <FiCheckCircle className="w-4 h-4" />;
            case 'cancel': return <FiXCircle className="w-4 h-4" />;
            default: return <FiAlertCircle className="w-4 h-4" />;
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch {
            return dateStr;
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(parseInt(timestamp) * 1000);
        return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    };

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Export to CSV
    const exportToCSV = () => {
        const headers = ['Order ID', 'Type', 'Amount', 'Status', 'Date', 'Time', 'User ID', 'User Name', 'User Email', 'User Mobile'];
        const csvData = filteredOrders.map(order => [
            order.order_id,
            order.type,
            order.amount,
            order.status,
            order.date,
            formatTime(order.time),
            order.user_id || 'N/A',
            order.user_name || 'N/A',
            order.user_email || 'N/A',
            order.user_mobile || 'N/A'
        ]);

        const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `orders_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Export successful!');
    };

    // Skeleton Loader
    const SkeletonRow = () => (
        <tr className="animate-pulse">
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
            <td className="px-6 py-4"><div className="h-8 bg-gray-200 rounded w-20"></div></td>
        </tr>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">

            {/* Header */}
            <header className="fixed top-0 left-0 right-0 bg-white shadow-lg z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* Back Button */}
                        <button
                            onClick={() => navigate(-1)}
                            className="bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 p-2.5 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg group"
                        >
                            <FiArrowLeft className="w-5 h-5 text-gray-700 group-hover:text-gray-900" />
                        </button>

                        {/* Centered Title */}
                        <div className="flex items-center absolute left-1/2 transform -translate-x-1/2">
                            <span className="ml-3 text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                                Orders Management
                            </span>
                        </div>

                        {/* Empty div for spacing */}
                        <div className="w-10"></div>
                    </div>
                </div>
            </header>

            <main className="pt-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    {/* Statistics Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500 hover:shadow-lg transition-all duration-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Total Orders</p>
                                    <p className="text-2xl font-bold text-gray-800 mt-1">{statistics.totalOrders}</p>
                                </div>
                                <div className="bg-blue-100 rounded-full p-3">
                                    <FiPackage className="w-6 h-6 text-blue-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500 hover:shadow-lg transition-all duration-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Total Amount</p>
                                    <p className="text-2xl font-bold text-gray-800 mt-1">₹{statistics.totalAmount.toLocaleString()}</p>
                                </div>
                                <div className="bg-green-100 rounded-full p-3">
                                    <FiDollarSign className="w-6 h-6 text-green-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500 hover:shadow-lg transition-all duration-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Average Order Value</p>
                                    <p className="text-2xl font-bold text-gray-800 mt-1">₹{statistics.averageOrderValue.toFixed(2)}</p>
                                </div>
                                <div className="bg-purple-100 rounded-full p-3">
                                    <FiTrendingUp className="w-6 h-6 text-purple-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-500 hover:shadow-lg transition-all duration-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Completed Orders</p>
                                    <p className="text-2xl font-bold text-gray-800 mt-1">{statistics.statusBreakdown.Complete || 0}</p>
                                </div>
                                <div className="bg-orange-100 rounded-full p-3">
                                    <FiCheckCircle className="w-6 h-6 text-orange-600" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Simplified Filters Section */}
                    <div className="bg-white rounded-xl shadow-md mb-8 overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                            <h2 className="text-lg font-semibold text-white flex items-center">
                                <FiFilter className="mr-2" />
                                Filters
                            </h2>
                        </div>

                        <div className="p-6">
                            {/* 4-column grid for filters */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {/* Date Range Picker - 1/4 width */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                        <FiCalendar className="mr-1 text-blue-500" /> Date Range
                                    </label>
                                    <DatePicker
                                        selectsRange={true}
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={(update) => setDateRange(update)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        placeholderText="Select range"
                                        dateFormat="dd/MM/yyyy"
                                        isClearable
                                        wrapperClassName="w-full"  // ← Add this for the wrapper div
                                    />
                                </div>

                                {/* User ID Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                        <FiUser className="mr-1 text-blue-500" /> User ID
                                    </label>
                                    <input
                                        type="text"
                                        value={userId}
                                        onChange={(e) => setUserId(e.target.value)}
                                        placeholder="Enter user ID"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    />
                                </div>

                                {/* Order Type Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Order Type</label>
                                    <select
                                        value={selectedType}
                                        onChange={(e) => setSelectedType(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    >
                                        {orderTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Status Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                    <select
                                        value={selectedStatus}
                                        onChange={(e) => setSelectedStatus(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    >
                                        {orderStatuses.map(status => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Second row for search and actions */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                {/* Search by Order ID */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Search Order ID</label>
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Enter order ID"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    />
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-end space-x-2">
                                    <button
                                        onClick={resetFilters}
                                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 flex items-center shadow-sm hover:shadow-md"
                                    >
                                        <FiRefreshCw className="mr-2" />
                                        Reset
                                    </button>
                                    <button
                                        onClick={exportToCSV}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 flex items-center shadow-sm hover:shadow-md"
                                    >
                                        <FiDownload className="mr-2" />
                                        Export
                                    </button>
                                </div>
                            </div>

                            {/* Active Filters Display - Smooth chips */}
                            {(startDate || endDate || userId || selectedType !== 'ALL' || selectedStatus !== 'ALL' || searchTerm) && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <div className="flex flex-wrap gap-2">
                                        {startDate && endDate && (
                                            <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm rounded-full flex items-center shadow-sm">
                                                <FiCalendar className="mr-1 w-3 h-3" />
                                                {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
                                            </span>
                                        )}
                                        {userId && (
                                            <span className="px-3 py-1.5 bg-green-50 text-green-700 text-sm rounded-full flex items-center shadow-sm">
                                                <FiUser className="mr-1 w-3 h-3" />
                                                User: {userId}
                                            </span>
                                        )}
                                        {selectedType !== 'ALL' && (
                                            <span className="px-3 py-1.5 bg-purple-50 text-purple-700 text-sm rounded-full flex items-center shadow-sm">
                                                Type: {selectedType}
                                            </span>
                                        )}
                                        {selectedStatus !== 'ALL' && (
                                            <span className="px-3 py-1.5 bg-orange-50 text-orange-700 text-sm rounded-full flex items-center shadow-sm">
                                                Status: {selectedStatus}
                                            </span>
                                        )}
                                        {searchTerm && (
                                            <span className="px-3 py-1.5 bg-pink-50 text-pink-700 text-sm rounded-full flex items-center shadow-sm">
                                                Order ID: {searchTerm}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Orders Table */}
                    <div className="bg-white rounded-xl shadow-md overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                            <div className="flex justify-between items-center">
                                <h2 className="text-lg font-semibold text-gray-800">
                                    Orders List
                                    <span className="ml-2 text-sm font-normal text-gray-500">
                                        ({filteredOrders.length} orders)
                                    </span>
                                </h2>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[1000px]">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {loading ? (
                                        <>
                                            <SkeletonRow />
                                            <SkeletonRow />
                                            <SkeletonRow />
                                            <SkeletonRow />
                                            <SkeletonRow />
                                        </>
                                    ) : (
                                        <>
                                            {currentItems.map((order) => (
                                                <tr key={order.order_id} className="hover:bg-gray-50 transition-all duration-200">
                                                    <td className="px-6 py-4 text-sm font-medium text-gray-900 font-mono">
                                                        {order.order_id}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center space-x-2">
                                                            {getTypeIcon(order.type)}
                                                            <span className="text-sm text-gray-600">{order.type}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-semibold text-gray-800">
                                                        ₹{parseFloat(order.amount).toLocaleString()}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`px-2 py-1 text-xs rounded-full flex items-center space-x-1 w-fit ${getStatusColor(order.status)}`}>
                                                            {getStatusIcon(order.status)}
                                                            <span>{order.status}</span>
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-500">
                                                        {formatDate(order.date)}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-500">
                                                        {formatTime(order.time)}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="text-sm">
                                                            <div className="font-medium text-gray-900">{order.user_name || 'N/A'}</div>
                                                            <div className="text-xs text-gray-500">ID: {order.user_id || 'N/A'}</div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <button
                                                            onClick={() => navigate(`/order/${order.order_id}`)}
                                                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center space-x-1 transition-colors duration-200"
                                                        >
                                                            <FiEye className="w-4 h-4" />
                                                            <span>View</span>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {filteredOrders.length === 0 && (
                                                <tr>
                                                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                                                        No orders found matching your filters
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {!loading && filteredOrders.length > 0 && (
                            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                                <div className="flex items-center justify-between flex-wrap gap-4">
                                    <div className="text-sm text-gray-700">
                                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredOrders.length)} of {filteredOrders.length} orders
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => paginate(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className={`px-3 py-1 rounded-md transition-all duration-200 ${currentPage === 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                                        >
                                            <FiChevronLeft className="w-4 h-4" />
                                        </button>
                                        <div className="flex space-x-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }

                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => paginate(pageNum)}
                                                        className={`px-3 py-1 rounded-md transition-all duration-200 ${currentPage === pageNum ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <button
                                            onClick={() => paginate(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className={`px-3 py-1 rounded-md transition-all duration-200 ${currentPage === totalPages ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                                        >
                                            <FiChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            <Toaster
                position="top-right"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: '#363636',
                        color: '#fff',
                    },
                    success: {
                        duration: 3000,
                        iconTheme: {
                            primary: '#10B981',
                            secondary: '#fff',
                        },
                    },
                    error: {
                        duration: 4000,
                        iconTheme: {
                            primary: '#EF4444',
                            secondary: '#fff',
                        },
                    },
                }}
            />
        </div>
    );
}

export default Orders;